/**
 * Sağlık Hesaplayıcıları - Vanilla JavaScript Module
 * No jQuery, no external dependencies
 *
 * Calculators:
 * - BMI (Body Mass Index)
 * - HOMA-IR (Insulin Resistance)
 * - ASCVD (Pooled Cohort Equations 2013)
 * - Healthy Life Score (0-100 heuristic)
 * - Metabolic Age (estimate)
 * - Sleep Quality (PSQI-inspired 0-17)
 */

(function() {
    'use strict';
  
    // ============================================================================
    // ASCVD COEFFICIENT TABLES (Pooled Cohort Equations 2013)
    // ============================================================================
  
    const ASCVD_COEFFICIENTS = {
      white_female: {
        ln_age: -29.799,
        ln_age_squared: 4.884,
        ln_total_chol: 13.540,
        ln_age_total_chol: -3.114,
        ln_hdl: -13.578,
        ln_age_hdl: 3.149,
        ln_treated_sbp: 2.019,
        ln_age_treated_sbp: 0,
        ln_untreated_sbp: 1.957,
        ln_age_untreated_sbp: 0,
        smoker: 7.574,
        ln_age_smoker: -1.665,
        diabetes: 0.661,
        mean_coefficient: -29.18,
        baseline_survival: 0.9665
      },
      white_male: {
        ln_age: 12.344,
        ln_age_squared: 0,
        ln_total_chol: 11.853,
        ln_age_total_chol: -2.664,
        ln_hdl: -7.990,
        ln_age_hdl: 1.769,
        ln_treated_sbp: 1.797,
        ln_age_treated_sbp: 0,
        ln_untreated_sbp: 1.764,
        ln_age_untreated_sbp: 0,
        smoker: 7.837,
        ln_age_smoker: -1.795,
        diabetes: 0.658,
        mean_coefficient: 61.18,
        baseline_survival: 0.9144
      },
      african_american_female: {
        ln_age: 17.114,
        ln_age_squared: 0,
        ln_total_chol: 0.940,
        ln_age_total_chol: 0,
        ln_hdl: -18.920,
        ln_age_hdl: 4.475,
        ln_treated_sbp: 29.291,
        ln_age_treated_sbp: -6.432,
        ln_untreated_sbp: 27.820,
        ln_age_untreated_sbp: -6.087,
        smoker: 0.691,
        ln_age_smoker: 0,
        diabetes: 0.874,
        mean_coefficient: 86.61,
        baseline_survival: 0.9533
      },
      african_american_male: {
        ln_age: 2.469,
        ln_age_squared: 0,
        ln_total_chol: 0.302,
        ln_age_total_chol: 0,
        ln_hdl: -0.307,
        ln_age_hdl: 0,
        ln_treated_sbp: 1.916,
        ln_age_treated_sbp: 0,
        ln_untreated_sbp: 1.809,
        ln_age_untreated_sbp: 0,
        smoker: 0.549,
        ln_age_smoker: 0,
        diabetes: 0.645,
        mean_coefficient: 19.54,
        baseline_survival: 0.8954
      }
    };
  
    // ============================================================================
    // UTILITIES
    // ============================================================================
  
    const Utils = {
      /**
       * Parse number with locale-aware decimal separator
       * @param {string} str - Input string
       * @param {string} locale - 'tr' or 'en'
       * @returns {number|null}
       */
      parseNumber(str, locale = 'en') {
        if (!str || typeof str !== 'string') {
          if (typeof str === 'number') return str;
          return null;
        }
  
        // Turkish locale: replace comma with dot
        if (locale === 'tr') {
          str = str.replace(',', '.');
        }
  
        const num = parseFloat(str);
        return isNaN(num) ? null : num;
      },
  
      /**
       * Format number with locale-aware decimal separator
       * @param {number} num - Number to format
       * @param {number} digits - Decimal places
       * @param {string} locale - 'tr' or 'en'
       * @returns {string}
       */
      formatNumber(num, digits = 1, locale = 'en') {
        if (num === null || num === undefined || isNaN(num)) return '';
  
        const fixed = num.toFixed(digits);
  
        // Turkish locale: replace dot with comma
        if (locale === 'tr') {
          return fixed.replace('.', ',');
        }
  
        return fixed;
      },
  
      /**
       * Convert glucose between mg/dL and mmol/L
       * @param {number} value - Value to convert
       * @param {string} from - 'mgdl' or 'mmol'
       * @param {string} to - 'mgdl' or 'mmol'
       * @returns {number}
       */
      convertGlucose(value, from, to) {
        if (from === to) return value;
  
        // mg/dL to mmol/L: divide by 18
        // mmol/L to mg/dL: multiply by 18
        if (from === 'mmol' && to === 'mgdl') {
          return value * 18;
        } else if (from === 'mgdl' && to === 'mmol') {
          return value / 18;
        }
  
        return value;
      },
  
      /**
       * Convert cholesterol between mg/dL and mmol/L
       * @param {number} value - Value to convert
       * @param {string} from - 'mgdl' or 'mmol'
       * @param {string} to - 'mgdl' or 'mmol'
       * @returns {number}
       */
      convertCholesterol(value, from, to) {
        if (from === to) return value;
  
        // mg/dL to mmol/L: divide by 38.67
        // mmol/L to mg/dL: multiply by 38.67
        if (from === 'mmol' && to === 'mgdl') {
          return value * 38.67;
        } else if (from === 'mgdl' && to === 'mmol') {
          return value / 38.67;
        }
  
        return value;
      },
  
      /**
       * Clamp value between min and max
       * @param {number} value - Value to clamp
       * @param {number} min - Minimum value
       * @param {number} max - Maximum value
       * @returns {number}
       */
      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      },
  
      /**
       * Validate input against constraints
       * @param {HTMLInputElement} input - Input element
       * @param {number} value - Parsed value
       * @returns {object} {valid: boolean, message: string}
       */
      validateInput(input, value) {
        if (value === null || isNaN(value)) {
          return {
            valid: false,
            message: 'Lütfen geçerli bir sayı girin'
          };
        }
  
        const min = parseFloat(input.getAttribute('min'));
        const max = parseFloat(input.getAttribute('max'));
  
        if (!isNaN(min) && value < min) {
          return {
            valid: false,
            message: `Değer ${min} değerinden küçük olamaz`
          };
        }
  
        if (!isNaN(max) && value > max) {
          return {
            valid: false,
            message: `Değer ${max} değerinden büyük olamaz`
          };
        }
  
        return { valid: true, message: '' };
      },
  
      /**
       * Persist inputs to sessionStorage
       * @param {string} blockId - Block identifier
       * @param {object} data - Input data
       */
      persistInputs(blockId, data) {
        try {
          sessionStorage.setItem(`hc_inputs_${blockId}`, JSON.stringify(data));
        } catch (e) {
          console.warn('SessionStorage not available:', e);
        }
      },
  
      /**
       * Restore inputs from sessionStorage
       * @param {string} blockId - Block identifier
       * @returns {object|null}
       */
      restoreInputs(blockId) {
        try {
          const stored = sessionStorage.getItem(`hc_inputs_${blockId}`);
          return stored ? JSON.parse(stored) : null;
        } catch (e) {
          console.warn('SessionStorage not available:', e);
          return null;
        }
      },
  
      /**
       * Debounce function calls
       * @param {function} func - Function to debounce
       * @param {number} wait - Wait time in ms
       * @returns {function}
       */
      debounce(func, wait = 300) {
        let timeout;
        return function executedFunction(...args) {
          const later = () => {
            clearTimeout(timeout);
            func(...args);
          };
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
        };
      }
    };
  
    // ============================================================================
    // CALCULATORS
    // ============================================================================
  
    const Calculators = {
      /**
       * Calculate BMI
       * @param {number} weight_kg - Weight in kg
       * @param {number} height_cm - Height in cm
       * @param {object} thresholds - Category thresholds
       * @returns {object} {value, category}
       *
       * Test: 100 kg, 180 cm → 30.9 (Obez)
       */
      bmi(weight_kg, height_cm, thresholds = {}) {
        // Validate height to prevent division by zero
        if (height_cm <= 0) {
          return { error: true, message: 'Boy değeri pozitif olmalıdır' };
        }
  
        const height_m = height_cm / 100;
        const bmi = weight_kg / (height_m * height_m);
  
        const {
          underweight = 18.5,
          normal = 24.9,
          overweight = 29.9
        } = thresholds;
  
        let category = '';
        if (bmi < underweight) {
          category = 'Zayıf';
        } else if (bmi <= normal) {
          category = 'Normal';
        } else if (bmi <= overweight) {
          category = 'Fazla Kilolu';
        } else {
          category = 'Obez';
        }
  
        return { value: bmi, category };
      },
  
      /**
       * Calculate HOMA-IR
       * @param {number} glucose_mgdl - Fasting glucose in mg/dL
       * @param {number} insulin_uU_ml - Fasting insulin in µU/mL
       * @param {object} thresholds - Category thresholds
       * @returns {object} {value, category}
       *
       * Test: Glucose 100 mg/dL, Insulin 10 µU/mL → 2.47 (Normal/Orta sınırda)
       */
      homaIR(glucose_mgdl, insulin_uU_ml, thresholds = {}) {
        const homaIR = (glucose_mgdl * insulin_uU_ml) / 405;
  
        const {
          normal = 2.5,
          medium = 5.0
        } = thresholds;
  
        let category = '';
        if (homaIR < normal) {
          category = 'Normal';
        } else if (homaIR <= medium) {
          category = 'Orta (İnsülin Direnci Olabilir)';
        } else {
          category = 'Yüksek (İnsülin Direnci)';
        }
  
        return { value: homaIR, category };
      },
  
      /**
       * Calculate ASCVD 10-year risk (Pooled Cohort Equations 2013)
       * @param {object} params - Patient parameters
       * @returns {object} {riskPct, category, note}
       *
       * Test 1: 55y, Male, White, TC 200, HDL 45, SBP 130, HT=no, DM=no, Smoker=no → ~7.5% (Orta)
       * Test 2: 65y, Female, African American, TC 240, HDL 50, SBP 150, HT=yes, DM=yes, Smoker=yes → ~28% (Yüksek)
       */
      ascvd(params) {
        const {
          age,
          sex, // 'male' or 'female'
          race, // 'white', 'african_american', 'other'
          total_chol_mgdl,
          hdl_mgdl,
          sbp,
          on_ht_treatment, // boolean
          diabetes, // boolean
          smoker // boolean
        } = params;
  
        // Validate age range (40-79)
        if (age < 40 || age > 79) {
          return {
            error: true,
            message: 'ASCVD hesabı 40-79 yaş arası için geçerlidir'
          };
        }
  
        // Map "other" race to white coefficients
        let actualRace = race;
        let note = '';
        if (race === 'other') {
          actualRace = 'white';
          note = 'Diğer/Asya/Hispanik için Beyaz katsayıları kullanıldı.';
        }
  
        // Select coefficient table
        const key = `${actualRace}_${sex}`;
        const coef = ASCVD_COEFFICIENTS[key];
  
        if (!coef) {
          return {
            error: true,
            message: 'Geçersiz cinsiyet veya ırk kombinasyonu'
          };
        }
  
        // Calculate natural logarithms
        const ln_age = Math.log(age);
        const ln_total_chol = Math.log(total_chol_mgdl);
        const ln_hdl = Math.log(hdl_mgdl);
        const ln_sbp = Math.log(sbp);
  
        // Build individual sum
        let individual_sum = 0;
  
        individual_sum += coef.ln_age * ln_age;
        individual_sum += coef.ln_age_squared * ln_age * ln_age;
        individual_sum += coef.ln_total_chol * ln_total_chol;
        individual_sum += coef.ln_age_total_chol * ln_age * ln_total_chol;
        individual_sum += coef.ln_hdl * ln_hdl;
        individual_sum += coef.ln_age_hdl * ln_age * ln_hdl;
  
        // Blood pressure (treated vs untreated)
        if (on_ht_treatment) {
          individual_sum += coef.ln_treated_sbp * ln_sbp;
          individual_sum += coef.ln_age_treated_sbp * ln_age * ln_sbp;
        } else {
          individual_sum += coef.ln_untreated_sbp * ln_sbp;
          individual_sum += coef.ln_age_untreated_sbp * ln_age * ln_sbp;
        }
  
        // Smoker
        if (smoker) {
          individual_sum += coef.smoker;
          individual_sum += coef.ln_age_smoker * ln_age;
        }
  
        // Diabetes
        if (diabetes) {
          individual_sum += coef.diabetes;
        }
  
        // Calculate risk
        // Risk = 1 - S_baseline^exp(individual_sum - mean_coefficient)
        const exponent = individual_sum - coef.mean_coefficient;
        const risk = 1 - Math.pow(coef.baseline_survival, Math.exp(exponent));
        const riskPct = risk * 100;
  
        // Categorize risk
        let category = '';
        if (riskPct < 5) {
          category = 'Düşük Risk';
        } else if (riskPct < 7.5) {
          category = 'Sınırda Risk';
        } else if (riskPct < 20) {
          category = 'Orta Risk';
        } else {
          category = 'Yüksek Risk';
        }
  
        return { riskPct, category, note };
      },
  
      /**
       * Calculate Healthy Life Score (0-100)
       * @param {object} inputs - Lifestyle inputs
       * @param {object} weights - Parameter weights
       * @returns {object} {score, category}
       */
      healthyLifeScore(inputs, weights) {
        const {
          water_l_per_day,
          exercise_min_per_week,
          sleep_h,
          meals_per_day,
          smoker,
          stress_level
        } = inputs;
  
        const {
          water = 0.15,
          exercise = 0.25,
          sleep = 0.20,
          meals = 0.10,
          smoking = 0.15,
          stress = 0.15
        } = weights;
  
        // Normalize each parameter (0-100 scale)
  
        // Water: optimal 2-3L, max 100 at 3L+
        const waterScore = Math.min(100, (water_l_per_day / 3) * 100);
  
        // Exercise: optimal 150+ min/week (WHO recommendation)
        const exerciseScore = Math.min(100, (exercise_min_per_week / 150) * 100);
  
        // Sleep: optimal 7-9h, penalty for <6 or >10
        let sleepScore = 100;
        if (sleep_h < 7) {
          sleepScore = (sleep_h / 7) * 100;
        } else if (sleep_h > 9) {
          sleepScore = Math.max(50, 100 - ((sleep_h - 9) * 15));
        }
  
        // Meals: optimal 3-5, penalty for extremes
        let mealsScore = 100;
        if (meals_per_day < 3) {
          mealsScore = (meals_per_day / 3) * 100;
        } else if (meals_per_day > 5) {
          mealsScore = Math.max(50, 100 - ((meals_per_day - 5) * 10));
        }
  
        // Smoking: 0 if smoker, 100 if not
        const smokingScore = smoker ? 0 : 100;
  
        // Stress: inverse scale (0 stress = 100, 10 stress = 0)
        // Clamp to 0-10 range to prevent negative scores
        const stressScore = 100 - (Utils.clamp(stress_level, 0, 10) * 10);
  
        // Weighted sum
        const totalScore =
          waterScore * water +
          exerciseScore * exercise +
          sleepScore * sleep +
          mealsScore * meals +
          smokingScore * smoking +
          stressScore * stress;
  
        let category = '';
        if (totalScore >= 80) {
          category = 'Mükemmel';
        } else if (totalScore >= 60) {
          category = 'İyi';
        } else if (totalScore >= 40) {
          category = 'Orta';
        } else {
          category = 'Geliştirilmeli';
        }
  
        return { score: totalScore, category };
      },
  
      /**
       * Estimate Metabolic Age
       * @param {object} inputs - Health parameters
       * @param {object} weights - Parameter weights
       * @returns {object} {metabolicAge, delta, category}
       */
      metabolicAge(inputs, weights) {
        const {
          chronological_age,
          sex,
          height_cm,
          weight_kg,
          waist_cm,
          sbp,
          fasting_glucose_mgdl
        } = inputs;
  
        const {
          bmi = 0.25,
          waist = 0.25,
          sbp: sbpWeight = 0.25,
          glucose = 0.25
        } = weights;
  
        // Calculate BMI
        const height_m = height_cm / 100;
        const calculatedBMI = weight_kg / (height_m * height_m);
  
        // Scoring system (deviations from optimal add years)
        let ageAdjustment = 0;
  
        // BMI impact: optimal 18.5-24.9
        if (calculatedBMI < 18.5) {
          ageAdjustment += (18.5 - calculatedBMI) * 0.5 * bmi;
        } else if (calculatedBMI > 24.9) {
          ageAdjustment += (calculatedBMI - 24.9) * 0.8 * bmi;
        }
  
        // Waist circumference impact: optimal <94cm (M), <80cm (F)
        const optimalWaist = sex === 'male' ? 94 : 80;
        if (waist_cm > optimalWaist) {
          ageAdjustment += (waist_cm - optimalWaist) * 0.15 * waist;
        }
  
        // Blood pressure impact: optimal <120 mmHg
        if (sbp > 120) {
          ageAdjustment += (sbp - 120) * 0.1 * sbpWeight;
        }
  
        // Glucose impact: optimal 70-100 mg/dL
        if (fasting_glucose_mgdl < 70) {
          ageAdjustment += (70 - fasting_glucose_mgdl) * 0.05 * glucose;
        } else if (fasting_glucose_mgdl > 100) {
          ageAdjustment += (fasting_glucose_mgdl - 100) * 0.15 * glucose;
        }
  
        // Calculate metabolic age
        const metabolicAge = Math.round(chronological_age + ageAdjustment);
        const delta = metabolicAge - chronological_age;
  
        let category = '';
        if (delta <= -5) {
          category = 'Çok İyi (Yaşınızdan Genç)';
        } else if (delta < 0) {
          category = 'İyi (Yaşınızdan Hafif Genç)';
        } else if (delta <= 5) {
          category = 'Normal (Yaşınıza Uygun)';
        } else if (delta <= 10) {
          category = 'Dikkat (Yaşınızdan Yaşlı)';
        } else {
          category = 'Risk (Yaşınızdan Çok Yaşlı)';
        }
  
        return { metabolicAge, delta, category };
      },
  
      /**
       * Calculate Sleep Quality Score (PSQI-inspired, 0-21)
       * @param {object} inputs - Sleep parameters
       * @returns {object} {score, category}
       */
      sleepQuality(inputs) {
        const {
          sleep_duration_h,
          sleep_latency_min,
          nocturnal_awakenings,
          sleep_regular,
          daytime_sleepiness,
          satisfaction
        } = inputs;
  
        let score = 0;
  
        // Duration (0-3 points): optimal 7-9h
        if (sleep_duration_h < 5) {
          score += 3;
        } else if (sleep_duration_h < 6) {
          score += 2;
        } else if (sleep_duration_h < 7) {
          score += 1;
        } else if (sleep_duration_h > 9) {
          score += 1;
        }
        // 7-9h = 0 points (optimal)
  
        // Latency (0-3 points): optimal <15 min
        if (sleep_latency_min > 60) {
          score += 3;
        } else if (sleep_latency_min > 30) {
          score += 2;
        } else if (sleep_latency_min > 15) {
          score += 1;
        }
  
        // Awakenings (0-3 points): optimal 0-1
        if (nocturnal_awakenings > 5) {
          score += 3;
        } else if (nocturnal_awakenings > 3) {
          score += 2;
        } else if (nocturnal_awakenings > 1) {
          score += 1;
        }
  
        // Regularity (0-2 points): irregular = penalty
        if (!sleep_regular) {
          score += 2;
        }
  
        // Daytime sleepiness (0-3 points): 0-10 scale
        if (daytime_sleepiness > 7) {
          score += 3;
        } else if (daytime_sleepiness > 5) {
          score += 2;
        } else if (daytime_sleepiness > 3) {
          score += 1;
        }
  
        // Satisfaction (0-3 points): 1-5 Likert, inverted
        const satisfactionScore = 6 - parseInt(satisfaction);
        if (satisfactionScore > 4) {
          score += 3;
        } else if (satisfactionScore > 3) {
          score += 2;
        } else if (satisfactionScore > 2) {
          score += 1;
        }
  
        // Interpret score (lower is better)
        let category = '';
        if (score <= 5) {
          category = 'Çok İyi Uyku Kalitesi';
        } else if (score <= 10) {
          category = 'İyi Uyku Kalitesi';
        } else if (score <= 15) {
          category = 'Orta Uyku Kalitesi';
        } else {
          category = 'Kötü Uyku Kalitesi';
        }
  
        return { score, category };
      }
    };
  
    // ============================================================================
    // EVENT HANDLERS & INITIALIZATION
    // ============================================================================
  
    let sectionData = null;
  
    /**
     * Initialize calculators
     */
    function init() {
      const section = document.querySelector('[data-section-type="health-calculators"]');
      if (!section) return;
  
      // Extract section-level configuration
      sectionData = {
        unitMode: section.dataset.unitMode || 'metric_mgdl',
        decimalLocale: section.dataset.decimalLocale || 'tr',
        enableSessionStorage: section.dataset.enableSessionStorage === 'true',
        enableCopyResult: section.dataset.enableCopyResult === 'true'
      };
  
      // Attach form submit handlers
      const forms = section.querySelectorAll('[data-calculator-form]');
      forms.forEach(form => {
        form.addEventListener('submit', handleCalculate);
  
        // Restore inputs if enabled
        if (sectionData.enableSessionStorage) {
          const blockId = form.dataset.blockId;
          const restored = Utils.restoreInputs(blockId);
          if (restored) {
            Object.keys(restored).forEach(name => {
              const input = form.querySelector(`[name="${name}"]`);
              if (input) {
                if (input.type === 'checkbox') {
                  input.checked = restored[name];
                } else {
                  input.value = restored[name];
                }
              }
            });
          }
        }
      });
  
      // Range input live updates
      const rangeInputs = section.querySelectorAll('input[type="range"]');
      rangeInputs.forEach(input => {
        const output = input.nextElementSibling;
        if (output && output.tagName === 'OUTPUT') {
          input.addEventListener('input', () => {
            output.textContent = input.value;
          });
        }
      });
  
      // Copy result handlers
      if (sectionData.enableCopyResult) {
        const copyButtons = section.querySelectorAll('[data-copy-result]');
        copyButtons.forEach(btn => {
          btn.addEventListener('click', handleCopyResult);
        });
      }
  
      // Print handlers
      const printButtons = section.querySelectorAll('[data-print-result]');
      printButtons.forEach(btn => {
        btn.addEventListener('click', handlePrint);
      });
  
      // Smooth scroll for TOC
      const tocLinks = section.querySelectorAll('.health-calculators__toc-link');
      tocLinks.forEach(link => {
        link.addEventListener('click', handleTocClick);
      });
    }
  
    /**
     * Handle form submission
     */
    function handleCalculate(e) {
      e.preventDefault();
  
      const form = e.target;
      const blockId = form.dataset.blockId;
      const calculatorType = form.dataset.calculatorType;
      const roundingDigits = parseInt(form.dataset.roundingDigits) || 1;
  
      // Clear previous errors
      const errorArea = form.querySelector('.health-calculators__error');
      if (errorArea) {
        errorArea.textContent = '';
        errorArea.style.display = 'none';
      }
  
      // Collect form data
      const formData = new FormData(form);
      const inputs = {};
      let hasError = false;
  
      // Parse and validate inputs
      for (let [name, value] of formData.entries()) {
        const input = form.querySelector(`[name="${name}"]`);
  
        if (input.type === 'checkbox') {
          inputs[name] = input.checked;
        } else if (input.type === 'number' || input.type === 'range') {
          const parsed = Utils.parseNumber(value, sectionData.decimalLocale);
          const validation = Utils.validateInput(input, parsed);
  
          if (!validation.valid && input.required) {
            showError(form, validation.message);
            input.setAttribute('aria-invalid', 'true');
            hasError = true;
            break;
          }
  
          input.removeAttribute('aria-invalid');
          inputs[name] = parsed;
        } else {
          inputs[name] = value;
        }
      }
  
      if (hasError) return;
  
      // Persist inputs if enabled
      if (sectionData.enableSessionStorage) {
        const persistData = {};
        for (let [name, value] of formData.entries()) {
          persistData[name] = value;
        }
        Utils.persistInputs(blockId, persistData);
      }
  
      // Perform calculation
      let result;
      try {
        result = performCalculation(calculatorType, inputs, form);
      } catch (err) {
        showError(form, 'Hesaplama sırasında bir hata oluştu: ' + err.message);
        return;
      }
  
      // Display result
      if (result.error) {
        showError(form, result.message);
      } else {
        displayResult(form, result, roundingDigits);
      }
    }
  
    /**
     * Perform calculation based on type
     */
    function performCalculation(type, inputs, form) {
      // Unit conversions (if needed)
      const isMMOL = sectionData.unitMode === 'metric_mmol';
  
      switch (type) {
        case 'bmi': {
          // Extract thresholds
          const thresholds = {
            underweight: parseFloat(form.querySelector('[data-threshold-underweight]')?.value || 18.5),
            normal: parseFloat(form.querySelector('[data-threshold-normal]')?.value || 24.9),
            overweight: parseFloat(form.querySelector('[data-threshold-overweight]')?.value || 29.9)
          };
          return Calculators.bmi(inputs.weight_kg, inputs.height_cm, thresholds);
        }
  
        case 'homa_ir': {
          // Convert glucose if needed
          let glucose_mgdl = inputs.fasting_glucose;
          if (isMMOL) {
            glucose_mgdl = Utils.convertGlucose(inputs.fasting_glucose, 'mmol', 'mgdl');
          }
  
          const thresholds = {
            normal: parseFloat(form.querySelector('[data-threshold-normal]')?.value || 2.5),
            medium: parseFloat(form.querySelector('[data-threshold-medium]')?.value || 5.0)
          };
  
          return Calculators.homaIR(glucose_mgdl, inputs.fasting_insulin, thresholds);
        }
  
        case 'ascvd': {
          // Convert cholesterol if needed
          let total_chol_mgdl = inputs.total_chol;
          let hdl_mgdl = inputs.hdl;
  
          if (isMMOL) {
            total_chol_mgdl = Utils.convertCholesterol(inputs.total_chol, 'mmol', 'mgdl');
            hdl_mgdl = Utils.convertCholesterol(inputs.hdl, 'mmol', 'mgdl');
          }
  
          return Calculators.ascvd({
            age: inputs.age,
            sex: inputs.sex,
            race: inputs.race,
            total_chol_mgdl,
            hdl_mgdl,
            sbp: inputs.sbp,
            on_ht_treatment: inputs.on_ht_treatment || false,
            diabetes: inputs.diabetes || false,
            smoker: inputs.smoker || false
          });
        }
  
        case 'healthy_life_score': {
          const weights = {
            water: parseFloat(form.querySelector('[data-weight-water]')?.value || 0.15),
            exercise: parseFloat(form.querySelector('[data-weight-exercise]')?.value || 0.25),
            sleep: parseFloat(form.querySelector('[data-weight-sleep]')?.value || 0.20),
            meals: parseFloat(form.querySelector('[data-weight-meals]')?.value || 0.10),
            smoking: parseFloat(form.querySelector('[data-weight-smoking]')?.value || 0.15),
            stress: parseFloat(form.querySelector('[data-weight-stress]')?.value || 0.15)
          };
  
          return Calculators.healthyLifeScore({
            water_l_per_day: inputs.water_l_per_day,
            exercise_min_per_week: inputs.exercise_min_per_week,
            sleep_h: inputs.sleep_h,
            meals_per_day: inputs.meals_per_day,
            smoker: inputs.smoker || false,
            stress_level: inputs.stress_level
          }, weights);
        }
  
        case 'metabolic_age': {
          // Convert glucose if needed
          let glucose_mgdl = inputs.fasting_glucose;
          if (isMMOL) {
            glucose_mgdl = Utils.convertGlucose(inputs.fasting_glucose, 'mmol', 'mgdl');
          }
  
          const weights = {
            bmi: parseFloat(form.querySelector('[data-weight-bmi]')?.value || 0.25),
            waist: parseFloat(form.querySelector('[data-weight-waist]')?.value || 0.25),
            sbp: parseFloat(form.querySelector('[data-weight-sbp]')?.value || 0.25),
            glucose: parseFloat(form.querySelector('[data-weight-glucose]')?.value || 0.25)
          };
  
          return Calculators.metabolicAge({
            chronological_age: inputs.chronological_age,
            sex: inputs.sex,
            height_cm: inputs.height_cm,
            weight_kg: inputs.weight_kg,
            waist_cm: inputs.waist_cm,
            sbp: inputs.sbp,
            fasting_glucose_mgdl: glucose_mgdl
          }, weights);
        }
  
        case 'sleep_quality': {
          return Calculators.sleepQuality({
            sleep_duration_h: inputs.sleep_duration_h,
            sleep_latency_min: inputs.sleep_latency_min,
            nocturnal_awakenings: inputs.nocturnal_awakenings,
            sleep_regular: inputs.sleep_regular || false,
            daytime_sleepiness: inputs.daytime_sleepiness,
            satisfaction: inputs.satisfaction
          });
        }
  
        default:
          return { error: true, message: 'Bilinmeyen hesaplama tipi' };
      }
    }
  
    /**
     * Display calculation result
     */
    function displayResult(form, result, roundingDigits) {
      const card = form.closest('.health-calculators__card');
      const resultArea = card.querySelector('[data-result-area]');
      const resultContent = resultArea.querySelector('.health-calculators__result-content');
      const calculatorType = form.dataset.calculatorType;
  
      let html = '';
  
      // Format based on calculator type - compact format
      if (calculatorType === 'bmi') {
        const formatted = Utils.formatNumber(result.value, roundingDigits, sectionData.decimalLocale);
        html += `<p>VKİ: <strong>${formatted} kg/m²</strong></p>`;
        if (result.category) {
          html += `<p>Durum: <strong>${result.category}</strong></p>`;
        }
        // İdeal kilo hesaplama (18.5-24.9 arası BMI için)
        const heightM = parseFloat(form.querySelector('[name="height_cm"]').value) / 100;
        if (heightM > 0) {
          const minWeight = (18.5 * heightM * heightM).toFixed(0);
          const maxWeight = (24.9 * heightM * heightM).toFixed(0);
          html += `<p>İdeal Kilo Aralığı: <strong>${minWeight}-${maxWeight} kg</strong></p>`;
        }
      } else if (calculatorType === 'homa_ir') {
        const formatted = Utils.formatNumber(result.value, roundingDigits, sectionData.decimalLocale);
        html += `<p>HOMA-IR Değeri: <strong>${formatted}</strong></p>`;
        if (result.category) {
          html += `<p>Durum: <strong>${result.category}</strong></p>`;
        }
      } else if (calculatorType === 'ascvd') {
        const formatted = Utils.formatNumber(result.riskPct, roundingDigits, sectionData.decimalLocale);
        html += `<p>10 Yıllık Risk: <strong>%${formatted}</strong></p>`;
        if (result.category) {
          html += `<p>Risk Durumu: <strong>${result.category}</strong></p>`;
        }
      } else if (calculatorType === 'healthy_life_score') {
        const formatted = Utils.formatNumber(result.score, roundingDigits, sectionData.decimalLocale);
        html += `<p>Sağlıklı Yaşam Skoru: <strong>${formatted}/100</strong></p>`;
        if (result.category) {
          html += `<p>Durum: <strong>${result.category}</strong></p>`;
        }
      } else if (calculatorType === 'metabolic_age') {
        const chronoAge = form.querySelector('[name="chronological_age"]').value;
        html += `<p>Gerçek Yaş: <strong>${chronoAge}</strong></p>`;
        html += `<p>Metabolik Yaş: <strong>${result.metabolicAge}</strong></p>`;
        // VKİ hesaplama
        const weight = parseFloat(form.querySelector('[name="weight_kg"]').value);
        const heightCm = parseFloat(form.querySelector('[name="height_cm"]').value);
        if (weight > 0 && heightCm > 0) {
          const bmi = (weight / Math.pow(heightCm / 100, 2)).toFixed(1);
          html += `<p>VKİ: <strong>${bmi}</strong></p>`;
        }
      } else if (calculatorType === 'sleep_quality') {
        const formatted = Utils.formatNumber(result.score, roundingDigits, sectionData.decimalLocale);
        html += `<p>Uyku Kalitesi Skoru: <strong>${formatted}/21</strong></p>`;
        if (result.category) {
          html += `<p>Durum: <strong>${result.category}</strong></p>`;
        }
      }
  
      resultContent.innerHTML = html;
      resultArea.style.display = 'block';
  
      // Scroll to result (with reduced-motion check)
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      resultArea.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest'
      });
    }
  
    /**
     * Show error message
     */
    function showError(form, message) {
      const errorArea = form.querySelector('.health-calculators__error');
      if (errorArea) {
        errorArea.textContent = message;
        errorArea.style.display = 'block';
      }
    }
  
    /**
     * Copy result to clipboard
     */
    function handleCopyResult(e) {
      const button = e.currentTarget;
      const card = button.closest('.health-calculators__card');
      const resultContent = card.querySelector('.health-calculators__result-content');
  
      if (!resultContent) return;
  
      const text = resultContent.innerText;
  
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          const originalText = button.textContent;
          button.textContent = 'Kopyalandı!';
          setTimeout(() => {
            button.textContent = originalText;
          }, 2000);
        }).catch(err => {
          console.error('Clipboard copy failed:', err);
        });
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
  
        const originalText = button.textContent;
        button.textContent = 'Kopyalandı!';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      }
    }
  
    /**
     * Print result
     */
    function handlePrint() {
      window.print();
    }
  
    /**
     * Handle TOC smooth scroll
     */
    function handleTocClick(e) {
      e.preventDefault();
      const targetId = e.currentTarget.getAttribute('href');
      const target = document.querySelector(targetId);
  
      if (target) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start'
        });
      }
    }
  
    // Initialize on DOMContentLoaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  
    // Reinitialize on Shopify section load (theme editor)
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail.sectionId.includes('health-calculators')) {
        init();
      }
    });
  
  })();
  